import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { CreditPullTiersService } from '../services/creditPullTiersService';
import { LenderCreditPullTierDetail } from '../types/creditPullTiersTypes';

const tierAssignmentForm = {
  fields: {
    lenderName: {
      value: '',
      error: null,
      rule: '',
      label: 'TPO Name',
    },
    currentTier: {
      value: '',
      error: null,
      rule: 'required',
      label: 'Current Tier',
      message: 'Current Tier is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class TierAssignmentStore {
  globalStore: GlobalStore;
  private creditPullTiersService: CreditPullTiersService;
  private onSaveSuccess: () => void;

  isOpen: boolean = false;
  isLoading: boolean = false;
  lenderDetail: LenderCreditPullTierDetail | null = null;
  formStore: FormStore;

  constructor(globalStore: GlobalStore, onSaveSuccess: () => void) {
    this.globalStore = globalStore;
    this.creditPullTiersService = new CreditPullTiersService();
    this.onSaveSuccess = onSaveSuccess;
    this.formStore = new FormStore(tierAssignmentForm, globalStore);

    makeObservable(this, {
      isOpen: observable,
      isLoading: observable,
      lenderDetail: observable,
      formStore: observable,
      openDialog: action,
      closeDialog: action,
      fetchLenderDetail: flow,
      saveTierAssignment: flow,
    });
  }

  openDialog(lenderId: number) {
    this.isOpen = true;
    this.fetchLenderDetail(lenderId);
  }

  closeDialog() {
    this.isOpen = false;
    this.lenderDetail = null;
    this.formStore = new FormStore(tierAssignmentForm, this.globalStore);
  }

  *fetchLenderDetail(lenderId: number) {
    try {
      this.isLoading = true;
      const response = yield this.creditPullTiersService.getLenderDetail(lenderId);
      this.lenderDetail = response.data?.data ?? null;
      if (this.lenderDetail) {
        this.formStore.loadForm({
          lenderName: this.lenderDetail.lenderName,
          currentTier: this.lenderDetail.currentTier,
        });
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to load lender detail.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  *saveTierAssignment(): Generator<any, boolean, any> {
    if (!this.formStore.runFormValidationWithMessage()) return false;
    try {
      const { currentTier } = this.formStore.getFormValues();
      yield this.creditPullTiersService.patchLenderCreditPullTier(this.lenderDetail.lenderId, currentTier);
      this.closeDialog();
      this.onSaveSuccess();
      return true;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Failed to update lender tier.',
      });
      return false;
    }
  }
}
